import * as React from 'react';

import {Field, reduxForm, SubmissionError} from 'redux-form';
// @ts-expect-error
import type {FormProps} from 'redux-form/lib/types.js.flow';

import {Button} from 'shells/button/button';
import {Line} from 'shells/divider/line';
import {ErrorMessage} from 'shells/form/error-message';
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {Header} from 'shells/typography/header';
import {Subheader} from 'shells/typography/subheader';

import {SIGNUP_REDUX_FORM} from '../constants';
import {TinyIntroTextField} from '../fields/tiny-intro-text-field';
import {validateForAnyValue} from '../fields/validate-for-any-value';
import {OauthSection} from '../login/login-form/oauth-section';
import type {EmailSignupFormValues, SignupStartPage} from '../types';
import {isMobileSignup} from '../utils';

import {SignupReferralHeader} from './signup-referral-header';
import {SignupStartPageHeader} from './signup-source-header';

import styles from './signup-form.module.css';

const ERROR_INVALID_EMAIL_MESSAGE = 'invalid_email';

const validatePassword = (value: string) => {
    if (!value) return 'Please enter a password';

    return value.trim().length < 6 ? 'Please choose a more complex password' : undefined;
};

type SignUpType = 'email' | 'google';

type OwnProps = {
    onGoogleSignup: (token: string) => Promise<any>;
    onEmailSignup: (formValues: EmailSignupFormValues) => Promise<any>;
    // If Google rejects the identity of the user,
    // i.e. before we even try to authenticate with Nutshell
    onGoogleIdentityFailure: () => void;
    googleErrorMessage?: string;
    // If the user's email is already attached to an account,
    // we need to modify the UI to tell the user we are logging
    // them in.
    initialValues: {
        firstAndLastName?: string;
        email?: string;
    };
    isLoggingInWithGoogle: boolean;
    onMicrosoftSignup: (token: string) => Promise<any>;
    onMicrosoftIdentityFailure: (arg1?: Error | null | undefined) => void;
    microsoftErrorMessage?: string;
    isLoggingInWithMicrosoft: boolean;
    microsoftOauthUrl: string | null | undefined;
    psPartnerKey: string | null | undefined;
    signupStartPage: SignupStartPage;
};

type Props = OwnProps & FormProps;

type State = {
    showEmailSignupSpinner: boolean;
    primarySignupType: SignUpType;
    isLoggingInWithOauth: boolean;
};

export class SignupFormComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            showEmailSignupSpinner: false,
            primarySignupType: props.initialValues.email ? 'email' : 'google',
            isLoggingInWithOauth:
                this.props.isLoggingInWithGoogle || this.props.isLoggingInWithMicrosoft,
        };
    }

    render() {
        // Need to reset this here because these props can change after the component is mounted
        this.setState({
            isLoggingInWithOauth:
                this.props.isLoggingInWithGoogle || this.props.isLoggingInWithMicrosoft,
        });

        return (
            <React.Fragment>
                <form
                    onSubmit={this.props.handleSubmit((values: EmailSignupFormValues) => {
                        // Show loading spinner
                        this.setState({showEmailSignupSpinner: true});

                        return (
                            this.props
                                .onEmailSignup(values)
                                .then(() => {
                                    // Stop showing loading spinner
                                    this.setState({showEmailSignupSpinner: false});
                                })
                                // @ts-expect-error
                                .catch((err) => {
                                    // Stop showing loading spinner
                                    this.setState({showEmailSignupSpinner: false});

                                    if (err.message === ERROR_INVALID_EMAIL_MESSAGE) {
                                        throw new SubmissionError({
                                            email: 'This email doesn’t seem valid.  Please doublecheck it.',
                                        });
                                    }

                                    throw new SubmissionError({
                                        _error: err.message,
                                    });
                                })
                        );
                    })}
                >
                    {this.renderReferralSection()}
                    {this.renderHeaderText()}
                    {/* If Google is primary signup type, show the Oauth buttons at the top */}
                    {!isMobileSignup() && this.state.primarySignupType === 'google' && (
                        <React.Fragment>
                            {this.renderOauthButtons()}
                            {this.renderOrSeparator()}
                        </React.Fragment>
                    )}
                    {this.renderEmailFields()}
                    {this.renderEmailButton()}
                    {/* If Email is primary signup type, show the Oauth buttons at the bottom */}
                    {!isMobileSignup() && this.state.primarySignupType === 'email' && (
                        <React.Fragment>
                            {this.renderOrSeparator()}
                            {this.renderOauthButtons()}
                        </React.Fragment>
                    )}
                    {this.renderErrorMessage(this.props.error)}
                </form>
            </React.Fragment>
        );
    }

    renderHeaderText() {
        return this.props.initialValues.firstAndLastName ? (
            <React.Fragment>
                <Header>
                    Welcome, {getFirstNameFromFullName(this.props.initialValues.firstAndLastName)}!
                </Header>
                <Subheader>Let’s finish setting up your Nutshell trial…</Subheader>
            </React.Fragment>
        ) : (
            <React.Fragment>
                <Header>Start your free 14-day Nutshell trial</Header>
                <Subheader>No credit card required, cancel anytime</Subheader>
            </React.Fragment>
        );
    }

    renderEmailFields() {
        return (
            <div className={this.state.primarySignupType === 'email' ? styles['space-above'] : ''}>
                <Field
                    key='firstAndLastName' // Weird name, I know, but its so auto-fill doesnt fill it
                    name='firstAndLastName'
                    placeholder='John Appleseed'
                    title='Full name'
                    type='text'
                    // This seems like a backwards boolean, but if the
                    // primarySignupType _is_ email, that means we've
                    // already gathered name and email, so we want
                    // to prefocus password. But, if we don't have the
                    // user's name, we _do_ need to focus it.
                    autoFocus={
                        this.state.primarySignupType !== 'email' ||
                        !this.props.initialValues.firstAndLastName
                    }
                    validate={validateForAnyValue('Please enter a name')}
                    component={TinyIntroTextField}
                    disabled={
                        this.props.isLoggingInWithGoogle || this.props.isLoggingInWithMicrosoft
                    }
                    variant='redux-form'
                />
                <Field
                    key='email'
                    name='email'
                    required={true}
                    placeholder='john.appleseed@nutshell.com'
                    title='Email'
                    type='email'
                    component={TinyIntroTextField}
                    disabled={this.state.isLoggingInWithOauth}
                    variant='redux-form'
                />
                <Field
                    key='password'
                    name='password'
                    placeholder='··········'
                    title='Password'
                    type='password'
                    // This seems like a backwards boolean, but if the
                    // primarySignupType _is_ email, that means we've
                    // already gathered name and email, so we want
                    // to prefocus password. But, if we don't have the
                    // user's name, we _do_ need to focus it.
                    autoFocus={
                        this.state.primarySignupType === 'email' &&
                        this.props.initialValues.firstAndLastName
                    }
                    validate={validatePassword}
                    component={TinyIntroTextField}
                    disabled={this.state.isLoggingInWithOauth}
                    variant='redux-form'
                />
            </div>
        );
    }

    renderEmailButton() {
        return (
            <div
                className={
                    this.state.showEmailSignupSpinner ? styles['button-loading'] : styles.button
                }
            >
                <Button
                    type='submit'
                    size='xlarge'
                    style={{fontSize: 16, height: 51}} /* Matches textfield */
                    isFullWidth={true}
                    variant='primary'
                    disabled={this.state.showEmailSignupSpinner || this.state.isLoggingInWithOauth}
                >
                    <div className={styles['button-contents-container']}>
                        {this.state.showEmailSignupSpinner ? 'Signing up…' : 'Sign up'}
                        {this.state.showEmailSignupSpinner ? (
                            <div className={styles['loading-icon-container']}>
                                <LoadingIcon fill='#fff' size={26} />
                            </div>
                        ) : undefined}
                    </div>
                </Button>
            </div>
        );
    }

    renderOauthButtons() {
        return (
            <div className={styles['space-above']}>
                <OauthSection
                    onGoogleSubmit={this.props.onGoogleSignup}
                    onMicrosoftSubmit={this.props.onMicrosoftSignup}
                    onGoogleIdentityFailure={this.props.onGoogleIdentityFailure}
                    onMicrosoftIdentityFailure={this.props.onMicrosoftIdentityFailure}
                    googleErrorMessage={this.props.googleErrorMessage}
                    microsoftErrorMessage={this.props.microsoftErrorMessage}
                    microsoftOauthUrl={this.props.microsoftOauthUrl}
                    googleVariant={this.props.isLoggingInWithGoogle ? 'login' : 'signup'}
                    microsoftVariant={this.props.isLoggingInWithMicrosoft ? 'login' : 'signup'}
                    hasExistingAccount={this.state.isLoggingInWithOauth}
                    shouldShowDivider={false}
                />
            </div>
        );
    }

    renderOrSeparator() {
        return (
            <div key='seperator' className={styles.seperator}>
                <Line text='or' />
            </div>
        );
    }

    renderErrorMessage = (errorMessage?: string) => {
        if (!errorMessage) {
            return undefined;
        }

        return (
            <div className={styles['below-button-message']}>
                <ErrorMessage>{errorMessage}</ErrorMessage>
            </div>
        );
    };

    renderReferralSection() {
        if (this.props.psPartnerKey) {
            return <SignupReferralHeader psPartnerKey={this.props.psPartnerKey} />;
        }

        return <SignupStartPageHeader signupStartPage={this.props.signupStartPage} />;
    }
}

// Naive way to just get the first string from a full name
function getFirstNameFromFullName(fullName: string) {
    return fullName.split(' ')[0];
}

// @ts-expect-error
export const SignupForm: React.ComponentType<OwnProps> = reduxForm({
    form: SIGNUP_REDUX_FORM,
    destroyOnUnmount: true,
})(SignupFormComponent);
